import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from "react";
import { Row, Col, Input, FormGroup, Button, Form } from "reactstrap";
import Select from 'react-select';
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { getMerchants, getMerchantCountries, getMerchantCurrencies, verifyPfiUser } from "@/services/program/getMerchants";
import { searchCategories } from "@/services/program/getCategories";
import { useTranslation } from "react-i18next";
import store from "@/containers/App/store";
import { setCurrency } from "@/redux/actions/currencyActions";
import { setCountry } from "@/redux/actions/countryActions";
import { setAuthCurrency, setAuthCountry } from "@/containers/App/auth";
import GiftCardImage from "@/shared/components/GiftCardImage";

const DEFAULT_COUNTRY = {
  label: 'United States',
  value: 223,
};

const CATEGORY_FILTERS = [
  { id: 'crypto', labelKey: 'category_crypto' },
  { id: 'fashion-retail', labelKey: 'category_fashion_retail' },
  { id: 'food', labelKey: 'category_food' },
  { id: 'travel', labelKey: 'category_travel' },
  { id: 'prepaid', labelKey: 'category_prepaid' },
];

// Mirrors the backend's approved category mapping for the 5 persistent buttons. The
// merchant-list endpoint still doesn't filter on `category` server-side, so button and
// search-resolved category filtering (see `activeCategoryFilter`) and name/category search
// are both applied here, client-side, against the full country-filtered list fetched once
// per country change.
const CATEGORY_VALUE_MAP = {
  crypto: ['Cryptocurrency'],
  'fashion-retail': ['Fashion', 'Retail'],
  food: ['Food'],
  travel: ['Travel'],
  prepaid: ['Prepaid'],
};

// `categoryValues` is the resolved list of category names to allow, from either a button
// (CATEGORY_VALUE_MAP) or a picked category-search result — both funnel through the same
// unified `activeCategoryFilter` state so one always replaces the other (see spec).
const matchesCategory = (merchant, categoryValues) => {
  if (!categoryValues || categoryValues.length === 0) return true;
  const merchantCategory = (merchant?.category || '').trim().toLowerCase();
  return categoryValues.some((value) => value.toLowerCase() === merchantCategory);
};

const matchesSearch = (merchant, searchTerm) => {
  const normalized = (searchTerm || '').trim().toLowerCase();
  if (!normalized) return true;
  const name = (merchant?.name || '').toLowerCase();
  const category = (merchant?.category || '').toLowerCase();
  return name.includes(normalized) || category.includes(normalized);
};

const filterMerchants = (merchantList = [], { categoryValues, searchTerm } = {}) =>
  merchantList.filter(
    (merchant) => matchesCategory(merchant, categoryValues) && matchesSearch(merchant, searchTerm)
  );

const resolveDefaultCountry = (countries, preferredValue) =>
  countries.find((c) => c.value === preferredValue) ||
  countries.find((c) => c.value === DEFAULT_COUNTRY.value) ||
  countries[0];

const findCurrencyForCountry = (currencies, countryValue) =>
  currencies.find((cur) => cur.country_id === countryValue) || currencies[0];

const byMerchantName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

// Number of columns an auto-fill grid currently lays out, so a single row can be cut to the tiles
// that fit at full size. Null until measured (or where layout isn't available, e.g. jsdom), in
// which case callers show every tile.
const useGridColumnCount = (enabled) => {
  const [node, setNode] = useState(null);
  const [columns, setColumns] = useState(null);

  useLayoutEffect(() => {
    if (!enabled || !node) {
      setColumns(null);
      return undefined;
    }
    const measure = () => {
      const tracks = window.getComputedStyle(node).gridTemplateColumns;
      const count = tracks && tracks !== 'none' ? tracks.split(' ').filter(Boolean).length : 0;
      setColumns(count || null);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, node]);

  return [setNode, columns];
};

// `searchPlaceholder` lets a host page label the search (the GCM redeem page says "national").
// `groupByCategory` splits All Merchants into A–Z category groups (merchants A–Z within each).
// `toolbarAside` is shown beside the search/filter toolbar (the GCM redeem page's catalog card).
// `collapseToRows` keeps Featured to one row (tiles that don't fit are dropped) and All Merchants
// to one row behind a "View more" toggle, until a search or category filter is applied.
const BrowseMerchant = ({ organization, program, currency, country, cart, searchPlaceholder, groupByCategory = false, toolbarAside = null, collapseToRows = false }) => {
  const [merchants, setMerchants] = useState([]);
  const [featuredMerchants, setFeaturedMerchants] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [verifyingMerchantId, setVerifyingMerchantId] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  // Either a persistent-button selection or a picked category-search result; whichever was
  // chosen most recently wins, and there is at most one active at a time (see spec: "Replace
  // the currently active button or searched category when selected").
  // Shape: null | { source: 'button'|'search', id: string, values: string[] }
  const [activeCategoryFilter, setActiveCategoryFilter] = useState(null);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [debouncedCategorySearchTerm, setDebouncedCategorySearchTerm] = useState("");
  const [categorySearchOptions, setCategorySearchOptions] = useState([]);
  const [categorySearchValue, setCategorySearchValue] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [allMerchantsExpanded, setAllMerchantsExpanded] = useState(false);
  const latestRequestId = useRef(0);
  const { t } = useTranslation();
  let navigate = useNavigate();

  const LOGO_PUBLIC_URL = `${process.env.REACT_APP_API_STORAGE_URL}`;

  const normalizeMerchantPayload = (payload) =>
    payload?.merchants ? payload.merchants : payload || [];

  const extractFeaturedMerchants = (merchantList = []) =>
    merchantList.filter(
      (merchant) =>
        Number(merchant?.pivot?.featured) === 1 ||
        Number(merchant?.featured) === 1
    );

  const fetchMerchantLists = async (organizationId, programId, baseParams = "") => {
    const requestId = ++latestRequestId.current;
    const allPayload = await getMerchants(organizationId, programId, baseParams);
    const isStaleResponse = requestId !== latestRequestId.current;
    if (isStaleResponse) return;
    const allMerchants = normalizeMerchantPayload(allPayload);
    setMerchants(allMerchants);
    setFeaturedMerchants(extractFeaturedMerchants(allMerchants));
  };

  useEffect(() => {
    if (!selectedCountry && country?.value) {
      setSelectedCountry(country);
    }
  }, [country, selectedCountry]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCategorySearchTerm(categorySearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [categorySearchTerm]);

  useEffect(() => {
    const term = debouncedCategorySearchTerm.trim();
    if (!organization?.id) {
      setCategorySearchOptions([]);
      return;
    }
    let cancelled = false;
    searchCategories(organization.id, term)
      .then((results) => {
        if (cancelled) return;
        setCategorySearchOptions(results.map((c) => ({ value: c.name, label: c.name })));
      })
      .catch(() => {
        if (!cancelled) setCategorySearchOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedCategorySearchTerm, organization?.id]);

  useEffect(() => {
    if (!selectedCurrency && currency?.value) {
      setSelectedCurrency(currency);
    }
  }, [currency, selectedCurrency]);

  useEffect(() => {
    if (currencies.length === 1 && selectedCurrency?.value && currency?.value !== selectedCurrency.value) {
      store.dispatch(setCurrency(selectedCurrency));
      setAuthCurrency(selectedCurrency);
    }
  }, [currencies, currency, selectedCurrency]);

  useEffect(() => {
    if (countries.length === 1 && country?.value && selectedCountry?.value && country.value !== selectedCountry.value) {
      store.dispatch(setCountry(selectedCountry));
      setAuthCountry(selectedCountry);
    }
  }, [countries, country, selectedCountry]);

  useEffect(() => {
    if (!organization?.id || !program?.id || !selectedCountry?.value) return;

    const params = `&country_id=${encodeURIComponent(selectedCountry.value)}`;

    setLoading(true);
    fetchMerchantLists(organization.id, program.id, params)
      .catch((e) => console.error("error getting merchants", e))
      .finally(() => setLoading(false));
    // Depend on the country value, not the object, so re-seeding the same country does not refetch.
    // Name/category search and category-button filtering are applied client-side below, so they
    // are intentionally not deps here — they don't require a new fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, program, selectedCountry?.value]);

  useEffect(() => {
    const init = async () => {
      if (!organization || !program) return;

      setLoading(true);

      const [res, res2] = await Promise.all([
        getMerchantCountries(organization.id, program.id),
        getMerchantCurrencies(organization.id, program.id),
      ]);

      const countryList = res?.countries?.map((c) => ({
        value: c.country_id,
        label: c.country_name,
      })) ?? [];

      const currencyOptionList = res2?.map((c) => ({
        ...c,
        value: c.code,
        label: c.code,
      })) ?? [];

      setCountries(countryList);
      setCurrencies(res2 ?? []);
      setCurrencyOptions(currencyOptionList);

      const defaultCountry = resolveDefaultCountry(countryList, country?.value);

      if (defaultCountry) {
        const defaultCurrency = findCurrencyForCountry(currencyOptionList, defaultCountry.value);

        setSelectedCountry(defaultCountry);
        store.dispatch(setCountry(defaultCountry));
        setAuthCountry(defaultCountry);

        if (defaultCurrency) {
          setSelectedCurrency(defaultCurrency);
          store.dispatch(setCurrency(defaultCurrency));
          setAuthCurrency(defaultCurrency);
        }
      }

      // With a country selected, the merchant-fetch effect owns the loading state.
      if (!defaultCountry) {
        setLoading(false);
      }
    };
    init();
    // `country` is read but intentionally not a dep — it would loop with the setCountry dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, program]);

  const handleCategoryClick = (categoryId) => {
    setActiveCategoryFilter((prev) => {
      if (prev?.source === 'button' && prev.id === categoryId) return null;
      return { source: 'button', id: categoryId, values: CATEGORY_VALUE_MAP[categoryId] || [] };
    });
    setCategorySearchValue(null);
  };

  const handleCategorySearchChange = (option) => {
    setCategorySearchValue(option);
    if (!option) {
      setActiveCategoryFilter((prev) => (prev?.source === 'search' ? null : prev));
      return;
    }
    setActiveCategoryFilter({ source: 'search', id: option.value, values: [option.value] });
  };

  const blockCurrencySwitchIfCartHasItems = (nextCurrencyCode) => {
    if (cart?.items?.length > 0 && cart?.currency_code && nextCurrencyCode !== cart.currency_code) {
      alert("Empty your cart or complete the transaction before switching currencies.");
      return true;
    }
    return false;
  };

  const onChangeCountryOption = (e) => {
    const nextCurrency = currencyOptions.find((v) => v.country_id === e.value);
    if (!nextCurrency) return;
    if (blockCurrencySwitchIfCartHasItems(nextCurrency.code)) return;

    setSelectedCountry(e);
    setSelectedCurrency(nextCurrency);
    store.dispatch(setCountry(e));
    setAuthCountry(e);
    store.dispatch(setCurrency(nextCurrency));
    setAuthCurrency(nextCurrency);
  };

  const onChangeCurrencyOption = (e) => {
    if (blockCurrencySwitchIfCartHasItems(e.code)) return;

    const nextCountry = countries.find((v) => v.value === e.country_id);
    setSelectedCurrency(e);
    setSelectedCountry(nextCountry);
    store.dispatch(setCurrency(e));
    setAuthCurrency(e);
    store.dispatch(setCountry(nextCountry));
    setAuthCountry(nextCountry);
  };

  const handleMerchantClick = async (merchant) => {
    if (verifyingMerchantId) return;
    try {
      if (merchant.is_pfi_merchant) {
        setPageLoading(true);
        setVerifyingMerchantId(merchant.id);
        const result = await verifyPfiUser(merchant.pivot.program_id, merchant.id);
        if (result.status !== '200') {
          alert(result.message || "User verification required");
          return;
        }
        if (result?.url !== '') {
          window.location.href = result.url;
          return;
        }
      }
      navigate(`/participant/redeem/${merchant.id}`);
    } catch (error) {
      console.error(error);
      alert("Verification failed. Please try again.");
    } finally {
      setVerifyingMerchantId(null);
      setTimeout(() => setPageLoading(false), 1000);
    }
  };

  const DotsLoader = () => (
    <div className="dots-loader">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );

  const visibleMerchants = useMemo(
    () => filterMerchants(merchants, { categoryValues: activeCategoryFilter?.values, searchTerm: debouncedSearch }),
    [merchants, activeCategoryFilter, debouncedSearch]
  );
  const visibleFeaturedMerchants = useMemo(
    () => filterMerchants(featuredMerchants, { categoryValues: activeCategoryFilter?.values, searchTerm: debouncedSearch }),
    [featuredMerchants, activeCategoryFilter, debouncedSearch]
  );

  // Counts reflect the current search term (so they stay meaningful while typing),
  // but not the active category — each button shows what selecting it would yield.
  const categoryCounts = useMemo(() => {
    const counts = {};
    CATEGORY_FILTERS.forEach(({ id }) => {
      counts[id] = filterMerchants(merchants, { categoryValues: CATEGORY_VALUE_MAP[id], searchTerm: debouncedSearch }).length;
    });
    return counts;
  }, [merchants, debouncedSearch]);

  const hasActiveFilters = Boolean(activeCategoryFilter) || Boolean(debouncedSearch?.trim());

  // A search or category filter always shows every match, so only the unfiltered list collapses.
  const allMerchantsCollapsed = collapseToRows && !allMerchantsExpanded && !hasActiveFilters;
  const [featuredGridRef, featuredColumns] = useGridColumnCount(collapseToRows);
  const [allGridRef, allColumns] = useGridColumnCount(allMerchantsCollapsed);

  const featuredToShow = collapseToRows && featuredColumns
    ? visibleFeaturedMerchants.slice(0, featuredColumns)
    : visibleFeaturedMerchants;
  const sortedVisibleMerchants = useMemo(
    () => (allMerchantsCollapsed ? [...visibleMerchants].sort(byMerchantName) : visibleMerchants),
    [allMerchantsCollapsed, visibleMerchants]
  );
  const allMerchantsRow = allColumns ? sortedVisibleMerchants.slice(0, allColumns) : sortedVisibleMerchants;
  // Expanded keeps the toggle so the list can be collapsed again.
  const canExpandAllMerchants = collapseToRows && !hasActiveFilters
    && (allMerchantsExpanded || (allColumns !== null && visibleMerchants.length > allColumns));

  // [{ name, merchants }] sorted by category name, uncategorized merchants last as "Other".
  // A single group (e.g. no merchant has a category yet) renders as the plain grid.
  const merchantGroups = useMemo(() => {
    if (!groupByCategory) return null;
    const groups = {};
    visibleMerchants.forEach((merchant) => {
      const category = (merchant?.category || '').trim();
      (groups[category] = groups[category] || []).push(merchant);
    });
    const names = Object.keys(groups).sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
    if (names.length < 2) return null;
    return names.map((name) => ({ name: name || t('category_other'), merchants: groups[name].sort(byMerchantName) }));
  }, [groupByCategory, visibleMerchants, t]);

  const renderMerchantCard = (item, index) => (
    <div
      key={index}
      className="bm-card merchant-fade-in"
      onClick={() => handleMerchantClick(item)}
    >
      <div className="bm-card__logo">
        <GiftCardImage src={item.logo ? `${LOGO_PUBLIC_URL}/${item.logo}` : ""} name={item.name} />
      </div>
      {item.name && <span className="bm-card__name">{item.name}</span>}
    </div>
  );

  const showFeaturedSection = loading || visibleFeaturedMerchants.length > 0;

  return (
    <>
      {pageLoading && (
        <div className="full-page-loader">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3">Please wait…</p>
        </div>
      )}
      <div className="browse-merchants">

        {/* ── Filter bar ── */}
        <div className="bm-filters">
          <div className="bm-filters__search">
            <Input
              type="text"
              className="bm-filters__input"
              placeholder={searchPlaceholder || t('search_merchants')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {countries.length > 1 && (
            <div className="bm-filters__select">
              <Select
                options={countries}
                value={selectedCountry}
                onChange={onChangeCountryOption}
                placeholder={t('select_country')}
                isSearchable
              />
            </div>
          )}
          {currencyOptions.length > 1 && (
            <div className="bm-filters__select">
              <Select
                options={currencyOptions}
                value={selectedCurrency}
                onChange={onChangeCurrencyOption}
                placeholder={t('select_currency')}
                isSearchable
              />
            </div>
          )}
        </div>

        {/* ── Category search (matches category names and their keywords) ── */}
        <div className="bm-category-search">
          <Select
            options={categorySearchOptions}
            value={categorySearchValue}
            onChange={handleCategorySearchChange}
            onInputChange={(value) => setCategorySearchTerm(value)}
            placeholder={t('category_search')}
            isClearable
            isSearchable
            // The options list is already the server's resolved match set (name or keyword ->
            // category name); react-select's own label-substring filter would wrongly hide a
            // result like "Food" when the typed term was a keyword ("grocery") that isn't a
            // substring of the label at all.
            filterOption={() => true}
          />
        </div>

        {/* ── Category filter buttons ── */}
        <div className="bm-category-filters">
          {CATEGORY_FILTERS.map((category) => {
            const isActive = activeCategoryFilter?.source === 'button' && activeCategoryFilter.id === category.id;
            return (
              <button
                key={category.id}
                type="button"
                className={`bm-category-filters__btn${isActive ? ' bm-category-filters__btn--active' : ''}`}
                aria-pressed={isActive}
                onClick={() => handleCategoryClick(category.id)}
              >
                {t(category.labelKey)}
                <span className="bm-category-filters__count">{categoryCounts[category.id] ?? 0}</span>
              </button>
            );
          })}
        </div>

        {toolbarAside && <div className="bm-toolbar-aside">{toolbarAside}</div>}

        {/* ── Featured merchants ── */}
        {showFeaturedSection && (
          <section className="bm-section">
            <h2 className="bm-section__title">{t('featured_merchants')}</h2>
            <div ref={featuredGridRef} className="bm-grid bm-grid--featured">
              {loading ? (
                <div className="bm-loader"><DotsLoader /></div>
              ) : featuredToShow.map((item, index) => (
                <div
                  key={index}
                  className="bm-card merchant-fade-in"
                  onClick={() => handleMerchantClick(item)}
                >
                  <div className="bm-card__logo">
                    <GiftCardImage src={item.logo ? `${LOGO_PUBLIC_URL}/${item.logo}` : ""} name={item.name} />
                  </div>
                  {item.name && <span className="bm-card__name">{item.name}</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── All merchants ── */}
        <section className="bm-section">
          {showFeaturedSection && <h2 className="bm-section__title">{t('all_merchants')}</h2>}
          {!loading && allMerchantsCollapsed && visibleMerchants.length > 0 ? (
            <div ref={allGridRef} className="bm-grid">{allMerchantsRow.map(renderMerchantCard)}</div>
          ) : !loading && merchantGroups ? (
            merchantGroups.map((group) => (
              <div key={group.name} className="bm-group">
                <h3 className="bm-group__title">
                  {group.name} <span className="bm-group__count">{group.merchants.length}</span>
                </h3>
                <div className="bm-grid">{group.merchants.map(renderMerchantCard)}</div>
              </div>
            ))
          ) : (
            <div className="bm-grid">
              {loading ? (
                <div className="bm-loader"><DotsLoader /></div>
              ) : visibleMerchants.length > 0 ? visibleMerchants.map(renderMerchantCard) : (
                <p className="bm-empty">
                  {hasActiveFilters ? t('no_merchants_match_filters') : t('no_merchants_found')}
                </p>
              )}
            </div>
          )}
          {!loading && canExpandAllMerchants && (
            <div className="bm-view-more">
              <button
                type="button"
                className="bm-view-more__btn"
                aria-expanded={allMerchantsExpanded}
                onClick={() => setAllMerchantsExpanded((expanded) => !expanded)}
              >
                {allMerchantsExpanded ? t('view_less') : t('view_more', { count: visibleMerchants.length - allMerchantsRow.length })}
              </button>
            </div>
          )}
        </section>

      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    organization: state.organization,
    program: state.program,
    currency: state.currency,
    country: state.country,
    cart: state.cart,
  };
};

export default connect(mapStateToProps)(BrowseMerchant);
